// Platform-agnostic program-editor model + transforms.
//
// The program editor renders twice — once on the legacy DOM web
// (apps/web/src/programs/ProgramEditor.tsx) and once as a shared
// react-native component (@jackedlogs/ui, consumed by apps/web-rnw and,
// later, apps/mobile). This module holds the matrix draft model and the
// pivot / validation logic so both renderers agree and the behaviour is
// unit-testable without a DOM or a native runtime. Keep it free of React
// and any UI import.
//
// Phase 5.2.2 — per-week per-set matrix.
//
// Each slot is a matrix: rows are set positions (uniform across weeks),
// columns are the cycle's weeks. Each cell carries reps + an intensity
// value; the kind of intensity is per-row (set position), so set 1 can
// be "% of MAX" while set 2 is "RPE" if the coach wants. Within a row,
// only the value changes across weeks, which is the common case (top
// set ramps 75/80/85/90; back-off sits at 65 the whole cycle).

import {
  type GroupCadence as GroupCadenceT,
  type NewProgram as NewProgramT,
  type Program as ProgramT,
  type ProgramStatus as ProgramStatusT,
  type SetIntensity as SetIntensityT,
  type SetWork as SetWorkT,
  type Variant as VariantT,
} from "./schemas/program.js";
import { intensityToCell } from "./programEditBuild.js";
import {
  authoredLocaleOf,
  authoredTextIn,
  pickAuthoredText,
  type AuthoredText as AuthoredTextT,
} from "./authoredText.js";
import { coerceLocale, type Locale } from "./locale.js";
import type { UnitsPreference } from "./schemas/profile.js";

export type SlotGroupDraft = {
  id: string;
  kind: "superset" | "circuit";
  // The group's label, round-tripped like cadence below. Optional: absent
  // means the client renders its own default ("Superset N"), which is what
  // every program written before PE-30 carries.
  name?: string;
  // Stored cadence, carried verbatim (HI-1). The editor cannot author it
  // yet — it lands with the circuit slice — but this draft is rebuilt
  // field-by-field on save, so WITHOUT this the first edit of a grouped
  // slot would silently wipe a cadence the coach set elsewhere. Same
  // lossless-round-trip rule as variantsRaw / notesRawPerCell.
  cadence?: GroupCadenceT;
};

export type IntensityKind = SetIntensityT["kind"];

// Form state mirrors the matrix shape but stores everything as strings
// so the input fields don't fight with intermediate edits ("8.", "0.5",
// empty during typing). Conversion to numbers happens in buildPayload.
export type CellDraft = {
  reps: string;
  intensityValue: string; // unused when kind = "none"
  // Time axis (HI-1), strings like every other cell field so the inputs
  // bind directly. Both absent on a reps set, which is every set written
  // before this delta. workDurationSec is read only when the ROW's
  // workKind is "countdown" — a countup bout has no prescribed length.
  workDurationSec?: string;
  restSec?: string;
};

export type SetRowDraft = {
  intensityKind: IntensityKind;
  // How this set is BOUNDED, on the ROW exactly like intensityKind is:
  // the shape of the set is a property of the set, its numbers vary by
  // week. Absent = a reps set (today's only case).
  workKind?: SetWorkT["kind"];
  cells: CellDraft[]; // length = cycleWeeks
  notesPerCell: string[]; // length = cycleWeeks; rarely populated
  // PE-21 — the stored value of each cell's note, kept verbatim so an
  // edit round-trip never destroys a translation the editor isn't
  // showing. Same lossless-round-trip rule as `variantsRaw` below.
  //
  // OPTIONAL, and every *Raw companion below follows the same rule:
  // absent means "nothing stored behind this string", which is exactly
  // what a freshly created row, a plan, or an expanded block has. The
  // build path then emits a naked string — the pre-PE-21 behaviour, and
  // the one that can never destroy a translation, because there is none.
  notesRawPerCell?: (AuthoredTextT | undefined)[];
};

export type SlotDraft = {
  id: string;
  exerciseId: string;
  exerciseName: string | undefined;
  setRows: SetRowDraft[];
  instructions: string;
  // Stored `instructions` verbatim — see notesRawPerCell.
  instructionsRaw?: AuthoredTextT;
  amrapLastSet: boolean;
  maxAttempt: boolean;
  // Propagated onto the resolved PlannedExerciseEntry.rpeEnabled by
  // programResolution so the Logger opens with the RPE column on for
  // this slot (Logger redesign §6.3).
  rpeEnabled: boolean;
  // Same propagation as rpeEnabled (Phase 8). Carried so an edit
  // round-trip never silently clears it.
  rirEnabled: boolean;
  group: SlotGroupDraft | null;
  // The stored slot's full variants array, preserved verbatim so an
  // edit round-trip never destroys multi-variant cadence the editor
  // can't express yet (lossless-round-trip rule,
  // next/program-editor-redesign.md §3.2). The editor only reads/writes
  // exerciseId/exerciseName; buildPayload grafts those onto variants[0]
  // and re-emits the rest untouched. Empty on freshly created slots.
  variantsRaw: VariantT[];
  // inactiveWeeks[w] = true → this slot doesn't run in week w (stored
  // as weeks[w].sets = []). Length = cycleWeeks. The matrix UI doesn't
  // expose it; the scheduler-composer drives it via block week chips.
  inactiveWeeks: boolean[];
};

export type SessionDraft = {
  id: string;
  name: string;
  notes: string;
  // Stored values verbatim — see SetRowDraft.notesRawPerCell.
  nameRaw?: AuthoredTextT;
  notesRaw?: AuthoredTextT;
  slots: SlotDraft[];
};

export type Draft = {
  name: string;
  description: string;
  // Stored values verbatim — see SetRowDraft.notesRawPerCell.
  nameRaw?: AuthoredTextT;
  descriptionRaw?: AuthoredTextT;
  // PE-21 — the language the editor is WRITING. On an existing program
  // it is the language the coach authored it in, so opening a
  // translated program puts them back on their own words rather than on
  // a translation of them. buildPayload grafts every edited string into
  // THIS language of the stored value, leaving the others alone.
  //
  // `null` on a Plan draft, which shares this type but has no localised
  // fields at all: with every *Raw companion absent, the build path
  // emits naked strings and the language is genuinely inert. Saying so
  // beats stamping a default language a plan never chose. Absent is
  // read the same way: no writing language, so naked strings out.
  editLocale?: Locale | null;
  // The language the program DECLARES it was written in — what gets
  // saved as `Program.authorLocale`.
  //
  // ⛔ Deliberately NOT the same field as `editLocale`. Looking at the
  // French tab is not a claim that the program is French: if the two
  // were one field, a coach who tapped through to check a translation
  // and then saved would silently reassign the program's canonical
  // language, and that language is the tie-breaker every reader in the
  // app falls back on. It changes only when somebody declares it.
  declaredAuthorLocale?: Locale;
  status: ProgramStatusT;
  cycleWeeks: number;
  sessionsPerWeek: number;
  sessions: SessionDraft[];
  // Existing assignee subs preserved across PATCH (otherwise the
  // server's "assignedTo omitted → [callerSub]" default would silently
  // drop every athlete the coach already shared the program with).
  // On create this stays empty and the server fills [callerSub].
  assignedTo: string[];
  // New athlete emails the coach is adding this round. PlansCard
  // pattern: parallel field to assignedTo, server resolves through
  // Cognito + the edge-based permission gate. Empty when no new
  // additions.
  assigneeEmails: string[];
};

// PE-21 — a stored value only survives a text change when the coach is
// EDITING that text in their own language. When a text is REPLACED
// wholesale (a block applied over an existing slot, a member's cue
// written across weeks), the other languages describe the OLD text and
// become lies: an Italian cue replaced by "Esplosivo" would keep a
// French translation still reading "descends lentement". So the stored
// value is dropped exactly when the text actually changed, and kept
// when the write is a no-op.
export const rawForReplacedText = <T>(
  previousText: string,
  nextText: string,
  raw: T | undefined,
): T | undefined => (previousText === nextText ? raw : undefined);

export const blankCell = (): CellDraft => ({ reps: "5", intensityValue: "70" });

export const blankSetRow = (cycleWeeks: number): SetRowDraft => ({
  intensityKind: "percent-of-max",
  cells: Array.from({ length: cycleWeeks }, blankCell),
  notesPerCell: Array.from({ length: cycleWeeks }, () => ""),
});

export const blankSlot = (cycleWeeks: number): SlotDraft => ({
  id: "",
  exerciseId: "",
  exerciseName: undefined,
  setRows: [blankSetRow(cycleWeeks)],
  instructions: "",
  amrapLastSet: false,
  maxAttempt: false,
  rpeEnabled: false,
  rirEnabled: false,
  group: null,
  variantsRaw: [],
  inactiveWeeks: Array.from({ length: cycleWeeks }, () => false),
});

// Convert a stored Program into the matrix-shaped editor draft.
// Single-variant only on read: variants[0] gets the picker; extras are
// preserved in the wire shape but not exposed yet (5.2.3).
//
// `cellUnit` is the unit the absolute-weight cells come out in. Weights are
// stored in kg (units.ts), so an lb user has to READ lb in the cell or the
// number under the LB header would still be kilos. Whatever is passed here
// MUST be passed to buildPayload on the way back, or the round-trip scales
// every prescription by 2.2 — useProgramEditor captures it once, at seed.
// Default "kg" is the identity conversion, so an un-updated caller is
// unchanged rather than wrong.
export const programToDraft = (
  program: ProgramT,
  cellUnit: UnitsPreference = "kg",
  // PE-21 — reader's language, used only to pick the editing language of
  // a program that never recorded an author. An existing program's own
  // language always wins.
  readerLocale?: Locale,
): Draft => {
  // The language the editor WRITES. `null` means we genuinely don't know:
  // the program carries naked strings and never recorded an author, which
  // is every program written before PE-21.
  //
  // ⛔ It must NOT fall back to the reader's language. That would let a
  // French reader open an Italian program, see "Français" as the active
  // language, and — the moment they corrected it — file the Italian text
  // under `fr` for ever. We don't know, so we say we don't know: with
  // `null` the draft behaves exactly as it did before PE-21 (naked
  // strings in, naked strings out) and the language chip stays out of the
  // way until somebody declares the language.
  //
  // ⭐ L'eccezione e' il programma NUOVO: un nome ancora vuoto non ha una
  // lingua da marcare male, e cio' che il coach sta per scrivere e' per
  // definizione nella sua. E' l'unico momento in cui la lingua d'autore
  // si conosce con certezza, quindi e' li' che si registra.
  const isBlank =
    program.name === undefined ||
    (typeof program.name === "string" && program.name.trim().length === 0);
  //
  // ⭐ PE-32 — e il lettore viene PRIMA dell'autore quando il programma ha
  // davvero testo nella sua lingua. Suo smoke del 29/08: «io ho l'UI in
  // inglese, ma l'editor mi mostra l'italiano». `authoredLocaleOf` preferisce
  // per costruzione la lingua d'autore, quindi il lettore non era mai
  // consultato e non c'era modo di scoprirlo.
  // ⛔ Il predicato si ancora all'OGGETTO per-lingua, non a `authoredTextIn`
  // da solo: su una stringa nuda senza `authorLocale` quella risponde «si',
  // ho testo» per QUALUNQUE lingua, e un programma italiano mai dichiarato si
  // aprirebbe marcato inglese — il Nodo 3 di PE-21 §6, cioè l'italiano
  // archiviato sotto la chiave sbagliata al primo salvataggio.
  // ⛔ E una traduzione a MACCHINA conta come testo, esattamente come una
  // scritta a mano. Sua decisione del 29/08 notte, dopo lo smoke di
  // `4.37.0-dev`: *«il default deve essere la lingua di chi edita, come tutto
  // il resto»*. La condizione contraria era stata aggiunta qui il giorno
  // prima su una premessa FALSA — «oggi nessuno scrive `autoTranslated`» —
  // mentre il backfill del 22/08 l'ha scritto su tutti e 15 i programmi di
  // prod (`tooling/migrations/2026-08-22-pe21-authored-text-backfill`,
  // `_common.py:126`). Risultato: i Tetras, tradotti da `it`, si aprivano in
  // italiano a un lettore inglese, ed e' il difetto che lui ha smokato.
  // ⭐ Cio' che rende sicuro non escluderle: correggere una traduzione le
  // TOGLIE il marchio (`setAuthoredLocale`, authoredText.ts:160), quindi il
  // testo corretto diventa suo; e la lingua attiva ora e' dichiarata a
  // schermo dal chip di `PE-32`, quindi non e' piu' invisibile.
  // ⛔ E «lettore ignoto» non e' «lettore inglese». `coerceLocale(undefined)`
  // risponde `en`, che per il ramo `isBlank` andava bene (un programma nuovo
  // va scritto in QUALCHE lingua) ma qui direbbe un'altra cosa: che l'inglese
  // batte la lingua d'autore per un chiamante che non ha mai dichiarato un
  // lettore. Chi non lo passa non esprime una preferenza, quindi non ne vince
  // nessuna. (Review consolidamento: `apps/web/src/programs/ProgramEditor.tsx:40`
  // e' il chiamante senza lettore.)
  const readerL = coerceLocale(readerLocale);
  const readerHasText =
    readerLocale !== undefined &&
    program.name != null &&
    typeof program.name !== "string" &&
    authoredTextIn(program.name, readerL) !== "";
  const editLocale =
    (readerHasText ? readerL : null) ??
    authoredLocaleOf(program.name, program.authorLocale) ??
    (isBlank ? readerL : null);
  // A blank program is being authored right now, so the language it is
  // being written in IS its author language. An existing one keeps
  // whatever it declared, including nothing.
  const declaredAuthorLocale =
    program.authorLocale ?? (isBlank ? (editLocale ?? undefined) : undefined);
  const text = (v: AuthoredTextT | string | undefined): string =>
    pickAuthoredText(v, editLocale ?? readerLocale, program.authorLocale) ?? "";
  return {
  name: text(program.name),
  description: text(program.description),
  nameRaw: program.name,
  descriptionRaw: program.description,
  editLocale,
  ...(declaredAuthorLocale ? { declaredAuthorLocale } : {}),
  status: program.status,
  cycleWeeks: program.cycleWeeks,
  sessionsPerWeek: program.sessionsPerWeek,
  // Stored programs always carry an assignedTo (min(1) on the schema).
  // On create-from-template the synth path passes `[""]`; treat that
  // as empty so we don't smuggle a sentinel string into the payload.
  assignedTo: program.assignedTo.filter((s) => s !== ""),
  // Chip input always starts empty — adds layer on top of the
  // preserved assignedTo via the resolveAssignees path on the server.
  assigneeEmails: [],
  sessions: program.sessions.map((s) => ({
    id: s.id,
    name: text(s.name),
    notes: text(s.notes),
    nameRaw: s.name,
    notesRaw: s.notes,
    slots: s.slots.map((slot) => {
      const v0 = slot.variants[0];
      // Inactive weeks (sets: []) carry "this slot doesn't run in week
      // w". The matrix shape is read from the first ACTIVE week.
      const inactiveWeeks = Array.from(
        { length: program.cycleWeeks },
        (_, w) => (slot.weeks[w]?.sets.length ?? 0) === 0,
      );
      const firstActive = inactiveWeeks.findIndex((off) => !off);
      // Pivot weeks[]→ rows[]: walk every week, collect each set
      // position into a row. We assume set count is uniform across
      // active weeks (validated server-side).
      const setCount =
        firstActive >= 0 ? (slot.weeks[firstActive]?.sets.length ?? 0) : 0;
      const rows: SetRowDraft[] = [];
      for (let r = 0; r < setCount; r++) {
        // The kind of the row is taken from the first active week, set r
        // (the typical case). If a downstream week's set has a different
        // kind, we adopt the row's kind on save and the user re-edits if
        // needed.
        const w0Set = slot.weeks[firstActive]?.sets[r];
        const kind: IntensityKind = w0Set?.intensity.kind ?? "none";
        // Same first-active-week convention as the intensity kind above:
        // the row carries one shape, the weeks carry the numbers.
        const workKind = w0Set?.work?.kind;
        const cells: CellDraft[] = [];
        const notesPerCell: string[] = [];
        const notesRawPerCell: (AuthoredTextT | undefined)[] = [];
        for (let w = 0; w < program.cycleWeeks; w++) {
          const cell = slot.weeks[w]?.sets[r];
          let reps = "0";
          let value = "";
          if (cell) {
            reps = `${cell.reps}${cell.amrap ? "+" : ""}`;
            // The inverse of buildIntensity, shared with the PE-22 K1
            // adapter so the two directions cannot drift apart.
            value = intensityToCell(cell.intensity, cellUnit).intensityValue;
          }
          const draftCell: CellDraft = { reps, intensityValue: value };
          if (cell?.work?.kind === "countdown")
            draftCell.workDurationSec = String(cell.work.durationSec);
          if (cell?.restSec !== undefined)
            draftCell.restSec = String(cell.restSec);
          cells.push(draftCell);
          notesPerCell.push(text(cell?.notes));
          notesRawPerCell.push(cell?.notes);
        }
        const row: SetRowDraft = {
          intensityKind: kind,
          cells,
          notesPerCell,
          notesRawPerCell,
        };
        if (workKind) row.workKind = workKind;
        rows.push(row);
      }
      // Empty slot (no sets recorded yet) — surface a single blank row
      // so the user can start typing.
      if (rows.length === 0) rows.push(blankSetRow(program.cycleWeeks));
      return {
        id: slot.id,
        exerciseId: v0?.exerciseId ?? "",
        exerciseName: v0?.exerciseName,
        setRows: rows,
        instructions: text(slot.instructions),
        instructionsRaw: slot.instructions,
        amrapLastSet: slot.amrapLastSet ?? false,
        maxAttempt: slot.maxAttempt ?? false,
        rpeEnabled: slot.rpeEnabled ?? false,
        rirEnabled: slot.rirEnabled ?? false,
        group: slot.group
          ? {
              id: slot.group.id,
              kind: slot.group.kind,
              ...(slot.group.cadence !== undefined
                ? { cadence: slot.group.cadence }
                : {}),
              ...(slot.group.name !== undefined
                ? { name: slot.group.name }
                : {}),
            }
          : null,
        variantsRaw: slot.variants,
        inactiveWeeks,
      };
    }),
  })),
  };
};

// Cell parsing + payload build/validation live in ./programEditBuild to
// keep this file under the 500-line cap. Re-exported here so the existing
// ./programEdit import paths (planEdit, tests, the shared index) keep
// working unchanged.
export {
  parseRepsToken,
  parseFloatLoose,
  buildIntensity,
  intensityToCell,
  VALUELESS_KINDS,
  seedForIntensityKind,
  buildPayload,
} from "./programEditBuild.js";
export {
  amrapWithTimeError,
  buildWorkForCell,
  timeAxisError,
} from "./programEditTimeAxis.js";
export type {
  IntensityErrorCode,
  BuildReasonCode,
  BuildResult,
} from "./programEditBuild.js";


// Synthesise a Program-shaped value from a NewProgram creation draft so
// programToDraft can produce the editor's internal Draft shape with no
// branching on create-vs-edit. The synthesised id stays empty — the
// editor uses that as the POST-vs-PATCH discriminator.
export const newProgramToProgram = (n: NewProgramT): ProgramT => ({
  id: "",
  createdBy: "",
  assignedTo: n.assignedTo && n.assignedTo.length > 0 ? n.assignedTo : [""],
  name: n.name,
  description: n.description,
  cycleWeeks: n.cycleWeeks,
  sessionsPerWeek: n.sessionsPerWeek,
  status: n.status ?? "draft",
  createdAt: new Date(0).toISOString(),
  updatedAt: new Date(0).toISOString(),
  sessions: n.sessions.map((s) => ({
    id: s.id ?? "",
    name: s.name,
    notes: s.notes,
    slots: s.slots.map((slot) => ({
      ...slot,
      id: slot.id ?? "",
    })),
  })),
});
